"use client";

import { useState } from "react";
import { toast } from "sonner";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

type CitationStyle = "apa" | "mla" | "chicago" | "harvard";

interface CitationResult {
  parenthetical: string;
  narrative: string;
}

// A page range or list ("15-20", "15–20", "15, 18") takes "pp."; one page takes "p."
// (APA 7 8.13; Cite Them Right Harvard).
const PAGE_RANGE_REGEX = /[-–—,&]|\band\b/;

function pageLabel(page: string): string {
  return PAGE_RANGE_REGEX.test(page) ? `pp. ${page}` : `p. ${page}`;
}

// An undated source is cited "n.d." in APA (9.17), Chicago author-date and Harvard.
function yearOrNoDate(year: string): string {
  return year.trim() || "n.d.";
}

function parseAuthors(input: string): string[] {
  return input
    .split(",")
    .map((a) => a.trim())
    .filter((a) => a.length > 0);
}

function generateApa(
  authors: string[],
  year: string,
  page: string
): CitationResult {
  const pagePart = page ? `, ${pageLabel(page)}` : "";
  if (authors.length === 0) {
    return { parenthetical: "", narrative: "" };
  }
  if (authors.length === 1) {
    return {
      parenthetical: `(${authors[0]}, ${year}${pagePart})`,
      narrative: `${authors[0]} (${year})`,
    };
  }
  if (authors.length === 2) {
    return {
      parenthetical: `(${authors[0]} & ${authors[1]}, ${year}${pagePart})`,
      narrative: `${authors[0]} and ${authors[1]} (${year})`,
    };
  }
  return {
    parenthetical: `(${authors[0]} et al., ${year}${pagePart})`,
    narrative: `${authors[0]} et al. (${year})`,
  };
}

function generateMla(
  authors: string[],
  _year: string,
  page: string
): CitationResult {
  const pagePart = page ? ` ${page}` : "";
  if (authors.length === 0) {
    return { parenthetical: "", narrative: "" };
  }
  if (authors.length === 1) {
    return {
      parenthetical: `(${authors[0]}${pagePart})`,
      narrative: `${authors[0]} states that ...${page ? ` (${page})` : ""}`,
    };
  }
  if (authors.length === 2) {
    return {
      parenthetical: `(${authors[0]} and ${authors[1]}${pagePart})`,
      narrative: `${authors[0]} and ${authors[1]} argue that ...${page ? ` (${page})` : ""}`,
    };
  }
  return {
    parenthetical: `(${authors[0]} et al.${pagePart})`,
    narrative: `${authors[0]} et al. argue that ...${page ? ` (${page})` : ""}`,
  };
}

function generateChicago(
  authors: string[],
  year: string,
  page: string
): CitationResult {
  const pagePart = page ? `, ${page}` : "";
  if (authors.length === 0) {
    return { parenthetical: "", narrative: "" };
  }
  if (authors.length === 1) {
    return {
      parenthetical: `(${authors[0]} ${year}${pagePart})`,
      narrative: `${authors[0]} (${year}${pagePart})`,
    };
  }
  if (authors.length === 2) {
    return {
      parenthetical: `(${authors[0]} and ${authors[1]} ${year}${pagePart})`,
      narrative: `${authors[0]} and ${authors[1]} (${year}${pagePart})`,
    };
  }
  // CMOS 18 (13.107): three or more authors take et al. from the first citation.
  return {
    parenthetical: `(${authors[0]} et al. ${year}${pagePart})`,
    narrative: `${authors[0]} et al. (${year}${pagePart})`,
  };
}

function generateHarvard(
  authors: string[],
  year: string,
  page: string
): CitationResult {
  const pagePart = page ? `, ${pageLabel(page)}` : "";
  if (authors.length === 0) {
    return { parenthetical: "", narrative: "" };
  }
  if (authors.length === 1) {
    return {
      parenthetical: `(${authors[0]}, ${year}${pagePart})`,
      narrative: `${authors[0]} (${year}${pagePart})`,
    };
  }
  if (authors.length === 2) {
    return {
      parenthetical: `(${authors[0]} and ${authors[1]}, ${year}${pagePart})`,
      narrative: `${authors[0]} and ${authors[1]} (${year}${pagePart})`,
    };
  }
  if (authors.length === 3) {
    return {
      parenthetical: `(${authors[0]}, ${authors[1]} and ${authors[2]}, ${year}${pagePart})`,
      narrative: `${authors[0]}, ${authors[1]} and ${authors[2]} (${year}${pagePart})`,
    };
  }
  return {
    parenthetical: `(${authors[0]} et al., ${year}${pagePart})`,
    narrative: `${authors[0]} et al. (${year}${pagePart})`,
  };
}

export function generateCitation(
  style: CitationStyle,
  authors: string[],
  year: string,
  page: string
): CitationResult {
  const trimmedPage = page.trim();
  switch (style) {
    case "mla":
      return generateMla(authors, year, trimmedPage);
    case "chicago":
      return generateChicago(authors, yearOrNoDate(year), trimmedPage);
    case "harvard":
      return generateHarvard(authors, yearOrNoDate(year), trimmedPage);
    default:
      return generateApa(authors, yearOrNoDate(year), trimmedPage);
  }
}

const STYLE_OPTIONS: { value: CitationStyle; label: string }[] = [
  { value: "apa", label: "APA 7th Edition" },
  { value: "mla", label: "MLA 9th Edition" },
  { value: "chicago", label: "Chicago Author-Date" },
  { value: "harvard", label: "Harvard" },
];

/** English strings. A translated content file overrides these through `ui`. */
export const IN_TEXT_CITATION_GENERATOR_STRINGS = {
  authorLastNameS: "Author last name(s)",
  eGSmithJohnsonWilliams: "e.g. Smith, Johnson, Williams",
  separateMultipleAuthorsWithCommas: "Separate multiple authors with commas",
  year: "Year",
  eG2024: "e.g. 2024",
  pageNumberOptional: "Page number (optional)",
  eG15: "e.g. 15",
  citationStyle: "Citation style",
  generatedCitations: "Generated Citations",
  parentheticalCitation: "Parenthetical citation",
  copied: "Copied!",
  copy: "Copy",
  useAtTheEndOf: "Use at the end of a sentence, before the period.",
  narrativeCitation: "Narrative citation",
  useWhenTheAuthorIs: "Use when the author is part of the sentence.",
  inTextCitationFormatsBy: "In-Text Citation Formats by Style",
  apa7thEdition: "APA 7th Edition",
  parentheticalAuthorYearPPage:
    "Parenthetical: (Author, Year, p. Page). Narrative: Author (Year). Use “&” for two authors in parenthetical form, “and” in narrative form. Three or more authors always use et al.",
  mla9thEdition: "MLA 9th Edition",
  parentheticalAuthorPageNoComma:
    "Parenthetical: (Author Page). No comma between author and page. No “p.” before page numbers. Narrative: Author states that ... (Page). Three or more authors use et al.",
  chicagoAuthorDate: "Chicago Author-Date",
  parentheticalAuthorYearPageNarrative:
    "Parenthetical: (Author Year, Page). Narrative: Author (Year, Page). Use “and” for two authors. Three or more authors use et al.",
  harvard: "Harvard",
  parentheticalAuthorYearPPage2:
    "Parenthetical: (Author, Year, p. Page). Narrative: Author (Year, p. Page). Similar to APA but uses “and” instead of “&” for two authors. Four or more authors use et al.",
} as const;

export default function InTextCitationGeneratorWidget() {
  const [authorsInput, setAuthorsInput] = useState("Smith");
  const [year, setYear] = useState("2024");
  const [page, setPage] = useState("");
  const [style, setStyle] = useState<CitationStyle>("apa");
  const [copied, setCopied] = useState<string | null>(null);

  const authors = parseAuthors(authorsInput);
  const result = generateCitation(style, authors, year, page);

  async function copyText(text: string, key: string) {
    if (await writeToClipboard(text)) {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } else {
      toast.error("Couldn't copy to clipboard. Please copy manually.");
    }
  }

  const strings = useToolUi(IN_TEXT_CITATION_GENERATOR_STRINGS);

  return (
    <>
      {/* Inputs */}
      <div className="space-y-4 rounded-lg border border-border bg-muted/30 p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="authors-input"
            >
              {strings.authorLastNameS}
            </label>
            <input
              className="w-full rounded-lg border border-border bg-card px-4 py-2 text-foreground text-sm placeholder:text-muted-foreground focus:border-foreground/30 focus:outline-none focus:ring-1 focus:ring-foreground/30"
              id="authors-input"
              onChange={(e) => setAuthorsInput(e.target.value)}
              placeholder={strings.eGSmithJohnsonWilliams}
              type="text"
              value={authorsInput}
            />
            <p className="text-muted-foreground text-xs">
              {strings.separateMultipleAuthorsWithCommas}
            </p>
          </div>

          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="year-input"
            >
              {strings.year}
            </label>
            <input
              className="w-full rounded-lg border border-border bg-card px-4 py-2 text-foreground text-sm placeholder:text-muted-foreground focus:border-foreground/30 focus:outline-none focus:ring-1 focus:ring-foreground/30"
              id="year-input"
              onChange={(e) => setYear(e.target.value)}
              placeholder={strings.eG2024}
              type="text"
              value={year}
            />
          </div>

          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="page-input"
            >
              {strings.pageNumberOptional}
            </label>
            <input
              className="w-full rounded-lg border border-border bg-card px-4 py-2 text-foreground text-sm placeholder:text-muted-foreground focus:border-foreground/30 focus:outline-none focus:ring-1 focus:ring-foreground/30"
              id="page-input"
              onChange={(e) => setPage(e.target.value)}
              placeholder={strings.eG15}
              type="text"
              value={page}
            />
          </div>
        </div>

        <div className="space-y-2">
          <label
            className="block font-medium text-foreground text-sm"
            htmlFor="style-select"
          >
            {strings.citationStyle}
          </label>
          <select
            className="w-full rounded-lg border border-border bg-card px-3 py-2 text-foreground text-sm focus:border-foreground/30 focus:outline-none focus:ring-1 focus:ring-foreground/30 sm:w-auto"
            id="style-select"
            onChange={(e) => setStyle(e.target.value as CitationStyle)}
            value={style}
          >
            {STYLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Results */}
      {/* MLA cites no year, and an undated source is "n.d." in the others. */}
      {authors.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.generatedCitations}
          </h2>

          <div className="space-y-3">
            <div className="rounded-lg border border-border bg-card p-5">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-medium text-foreground text-sm">
                  {strings.parentheticalCitation}
                </h3>
                <button
                  className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-muted hover:text-foreground"
                  onClick={() =>
                    copyText(result.parenthetical, "parenthetical")
                  }
                  type="button"
                >
                  {copied === "parenthetical" ? strings.copied : strings.copy}
                </button>
              </div>
              <p className="font-mono text-foreground text-sm leading-relaxed">
                {result.parenthetical}
              </p>
              <p className="mt-2 text-muted-foreground text-xs">
                {strings.useAtTheEndOf}
              </p>
            </div>

            <div className="rounded-lg border border-border bg-card p-5">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-medium text-foreground text-sm">
                  {strings.narrativeCitation}
                </h3>
                <button
                  className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-muted hover:text-foreground"
                  onClick={() => copyText(result.narrative, "narrative")}
                  type="button"
                >
                  {copied === "narrative" ? strings.copied : strings.copy}
                </button>
              </div>
              <p className="font-mono text-foreground text-sm leading-relaxed">
                {result.narrative}
              </p>
              <p className="mt-2 text-muted-foreground text-xs">
                {strings.useWhenTheAuthorIs}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Style comparison */}
      <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.inTextCitationFormatsBy}
        </h2>
        <div className="space-y-4 text-muted-foreground text-sm leading-relaxed">
          <div>
            <h3 className="font-medium text-foreground">
              {strings.apa7thEdition}
            </h3>
            <p>{strings.parentheticalAuthorYearPPage}</p>
          </div>
          <div>
            <h3 className="font-medium text-foreground">
              {strings.mla9thEdition}
            </h3>
            <p>{strings.parentheticalAuthorPageNoComma}</p>
          </div>
          <div>
            <h3 className="font-medium text-foreground">
              {strings.chicagoAuthorDate}
            </h3>
            <p>{strings.parentheticalAuthorYearPageNarrative}</p>
          </div>
          <div>
            <h3 className="font-medium text-foreground">{strings.harvard}</h3>
            <p>{strings.parentheticalAuthorYearPPage2}</p>
          </div>
        </div>
      </section>
      {/* Related tools */}
    </>
  );
}
